import { Injectable } from '@angular/core';
import {
	getMortgageLenderNamesForTitleInsurance,
	getMortgageName,
} from '@core/util/mortgage';
import { isSet } from '@core/util/value';
import { BusinessClient, ClientType, PersonClient } from '@models/clients';
import {
	BusinessMatterClient,
	Matter,
	MatterBusinessUser,
	MatterFileType,
	MatterTitle,
	PersonMatterClient,
	PropertyType,
} from '@models/matter';
import { MatterMortgageWithAssociations } from '@models/mortgage';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { Encumbrance } from '@models/encumbrance';
import { MortgageeType } from '@models/lender';
import { BusinessUser } from '@models/businesses';
import { clientLegalName } from '@core/util/client';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { checkIfPostalCodeRequired } from '@core/util/title-insurance';
import { TitleInsuranceProvider } from '@models/title-insurance';
import { GlobalProfileComponent } from 'src/app/private/global-profile/global-profile.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

const modalQueryParamKey = 'modal';
export const KEY_DATES_QUERY_PARAM_VALUE = 'keyDates';
export const PROFILE_ID_QUERY_PARAM_VALUE = 'profileId';

export interface MissingFieldSection {
	sectionName: string;
	fields: MissingField[];
}

export interface MissingField {
	openDrawer?: () => void;
	url?: string;
	fieldName: string;
	queryParams?: { [id: string]: string };
}

@Injectable({
	providedIn: 'root',
})
export class ValidateStewartDataService {
	constructor(private drawerService: DrawerService) {}

	getSubmitDataMissingSections(
		matter: Matter,
		clients: (PersonMatterClient | BusinessMatterClient)[],
		otherParty: (PersonMatterClient | BusinessMatterClient)[],
		mortgages: MatterMortgageWithAssociations[],
		remainEncumbrances: Encumbrance[],
		lawyers: BusinessUser[],
		matterTitles: MatterTitle[],
		nameFormatingSettings: NameFormattingSettings,
	): MissingFieldSection[] {
		const sections: MissingFieldSection[] = [];

		const lawyerSection: MissingFieldSection = {
			sectionName: 'Lawyer',
			fields: this.getLawyerMissingFields(
				matter.id,
				matter.lawyerAssigned,
				lawyers,
			),
		};

		if (lawyerSection.fields.length > 0) {
			sections.push(lawyerSection);
		}

		const transactionSection: MissingFieldSection = {
			sectionName: 'Transaction details',
			fields: this.getTransactionMissingFields(matter, otherParty),
		};

		if (transactionSection.fields.length > 0) {
			sections.push(transactionSection);
		}

		const clientSection: MissingFieldSection = {
			sectionName: `${matter.fileType === MatterFileType.PURCHASE ? 'Purchaser' : 'Mortgagor'} details`,
			fields: [],
		};

		if (clients.length === 0) {
			if (matter.fileType === MatterFileType.PURCHASE) {
				clientSection.fields.push({
					fieldName: 'No purchaser on file',
					url: UrlService.Matter_Parties(matter.id),
				});
			}
			if (matter.fileType === MatterFileType.REFINANCE) {
				clientSection.fields.push({
					fieldName: 'No mortgagor on file',
					url: UrlService.Matter_Parties_Mortgagors(matter.id),
				});
			}
		}

		if (!isSet(matter.capacity)) {
			clientSection.fields.push({
				fieldName: 'Capacity of ownership',
				url:
					matter.fileType === MatterFileType.PURCHASE
						? UrlService.Matter_Parties(matter.id)
						: UrlService.Matter_Parties_Mortgagors(matter.id),
			});
		}

		if (clientSection.fields.length > 0) {
			sections.push(clientSection);
		}

		clients.forEach((client) => {
			const clientMissingSection: MissingFieldSection = {
				sectionName: `Client -${clientLegalName(client, nameFormatingSettings)}`,
				fields: this.getClientMissingFields(matter.id, client),
			};

			if (clientMissingSection?.fields?.length > 0) {
				sections.push(clientMissingSection);
			}
		});

		const propertySection: MissingFieldSection = {
			sectionName: 'Property details',
			fields: this.getPropertyMissingFields(matter, matterTitles),
		};

		if (propertySection.fields.length > 0) {
			sections.push(propertySection);
		}

		const containsMMS = mortgages.some((x) => x?.mms);
		const filteredMortgages = mortgages.filter(
			(x) => isSet(x?.priority) && !x?.mms,
		);

		if (
			matter?.fileType === MatterFileType.REFINANCE &&
			!containsMMS &&
			filteredMortgages.length === 0
		) {
			sections.push({
				sectionName: 'Mortgage details',
				fields: [
					{
						fieldName: 'No mortgages on the file',
						url: UrlService.Matter_Mortgages(matter.id),
					},
				],
			});
		}

		filteredMortgages.forEach((mortgage) => {
			const mortgageMissingSection: MissingFieldSection = {
				sectionName: getMortgageName(mortgage),
				fields: this.getMortgageMissingFields(matter?.id, mortgage),
			};

			if (mortgageMissingSection?.fields?.length > 0) {
				sections.push(mortgageMissingSection);
			}
		});

		remainEncumbrances?.forEach((encumbrance: Encumbrance) => {
			const encumbranceMissingSection: MissingFieldSection = {
				sectionName: `Encumbrance -${encumbrance.priority}`,
				fields: this.getEncumbranceMissingFields(matter?.id, encumbrance),
			};

			if (encumbranceMissingSection?.fields?.length > 0) {
				sections.push(encumbranceMissingSection);
			}
		});

		return sections;
	}

	private getLawyerMissingFields(
		matterId: string,
		lawyer: MatterBusinessUser,
		lawyers: BusinessUser[],
	): MissingField[] {
		const result: MissingField[] = [];

		if (lawyer) {
			const _lawyerUser = lawyers.find((x) => x.id === lawyer.id);
			if (_lawyerUser && !isSet(_lawyerUser.stewartProfileId)) {
				result.push({
					fieldName: `No Stewart Profile ID assigned to ${lawyer.name}`,
					url: UrlService.Matter_TitleInsurance(matterId),
					queryParams: { [modalQueryParamKey]: PROFILE_ID_QUERY_PARAM_VALUE },
				});
			} else if (!_lawyerUser) {
				result.push({
					fieldName: 'Lawyer assigned to file is not an active Closer user',
					url: UrlService.Matter(matterId),
				});
			}
		} else {
			result.push({
				fieldName: 'No lawyer assigned',
				url: UrlService.Matter(matterId),
			});
		}

		return result;
	}

	private getEncumbranceMissingFields(
		matterId: string,
		encumbrance: Encumbrance,
	): MissingField[] {
		const fields: MissingField[] = [];
		const url = UrlService.Matter_Encumbrances(matterId);

		if (!isSet(encumbrance.mortgageeType)) {
			fields.push({
				fieldName: 'Mortgagee type',
				url,
			});
		} else if (encumbrance.mortgageeType === MortgageeType.INSTITUTION) {
			if (!isSet(encumbrance.financialInstitution?.name)) {
				fields.push({
					fieldName: 'Lender name',
					url,
				});
			}
		} else if (encumbrance.mortgageeType === MortgageeType.PRIVATE_LENDER) {
			if (!isSet(encumbrance.privateLender?.fullName)) {
				fields.push({
					fieldName: 'Lender name',
					url,
				});
			}
		}

		if (!isSet(encumbrance.outstandingAmount)) {
			fields.push({
				fieldName: 'Outstanding amount',
				url,
			});
		} else if (encumbrance.outstandingAmount === 0) {
			fields.push({
				fieldName: "Outstanding amount can't be $0.00",
				url,
			});
		}

		return fields;
	}

	private getClientMissingFields(
		matterId: string,
		client: PersonMatterClient | BusinessMatterClient,
	): MissingField[] {
		const fields: MissingField[] = [];
		const openDrawer = () => {
			this.drawerService.openDrawer(GlobalProfileComponent, {
				data: { clientId: client.id },
			});
		};

		if (client.type === ClientType.PERSON) {
			if (!isSet((client as PersonClient)?.lastName)) {
				fields.push({
					fieldName: 'Last name',
					openDrawer,
				});
			}
		} else if (!isSet((client as BusinessClient)?.name)) {
			fields.push({
				fieldName: 'Company name',
				openDrawer,
			});
		}

		if (client?.serviceAddress) {
			if (!isSet(client.serviceAddress?.street)) {
				fields.push({
					fieldName: 'Service address - street',
					openDrawer,
				});
			}

			if (!isSet(client.serviceAddress?.province)) {
				fields.push({
					fieldName: 'Service address - province',
					openDrawer,
				});
			}
		}

		return fields;
	}

	private getMortgageMissingFields(
		matterId: string,
		mortgage: MatterMortgageWithAssociations,
	): MissingField[] {
		const fields: MissingField[] = [];
		const url = UrlService.Matter_Mortgages_Mortgage(matterId, mortgage.id);
		const allLendersNames = getMortgageLenderNamesForTitleInsurance(mortgage);
		if (!allLendersNames) {
			fields.push({
				fieldName: 'Lender name',
				url,
			});
		}

		if (!isSet(mortgage?.amount)) {
			fields.push({
				fieldName: 'Amount',
				url,
			});
		} else if (mortgage?.amount === 0) {
			fields.push({
				fieldName: "Amount can't be $0.00",
				url,
			});
		}

		if (!isSet(mortgage?.referenceNumber)) {
			fields.push({
				fieldName: 'Reference number',
				url,
			});
		}

		return fields;
	}

	private getTransactionMissingFields(
		matter: Matter,
		otherParty: (PersonMatterClient | BusinessMatterClient)[],
	): MissingField[] {
		const fields: MissingField[] = [];

		if (!isSet(matter.closingDate)) {
			fields.push({
				fieldName: 'Closing date',
				url: UrlService.Matter_TitleInsurance(matter.id),
				queryParams: { [modalQueryParamKey]: KEY_DATES_QUERY_PARAM_VALUE },
			});
		}

		if (matter.fileType === MatterFileType.PURCHASE) {
			if (otherParty.length === 0) {
				fields.push({
					fieldName: 'No vendor on file',
					url: UrlService.Matter_Parties_OtherParties(matter.id),
				});
			}
			if (
				!(
					isSet(matter.otherPartyLawFirm) &&
					isSet(matter.otherPartyLawFirm?.lawyer)
				)
			) {
				fields.push({
					fieldName: 'Vendor lawyer',
					url: UrlService.Matter_Parties_OtherParties(matter.id),
				});
			}
		}

		return fields;
	}

	private getPropertyMissingFields(
		matter: Matter,
		matterTitles: MatterTitle[],
	): MissingField[] {
		const fields: MissingField[] = [];
		const url = UrlService.Matter_Property_Details(matter.id);

		if (matter.fileType === MatterFileType.PURCHASE) {
			if (!isSet(matter.purchasePrice) || matter.purchasePrice === 0) {
				fields.push({
					fieldName: 'Sale price',
					url: UrlService.Matter_Adjustments(matter.id),
				});
			}
		}

		if (isSet(matter.propertyDetails)) {
			if (!isSet(matter?.propertyDetails?.address?.street)) {
				fields.push({
					fieldName: 'Property address - street',
					url,
				});
			}

			if (!isSet(matter?.propertyDetails?.address?.city)) {
				fields.push({
					fieldName: 'Property address - city',
					url,
				});
			}

			if (
				!isSet(matter?.propertyDetails?.propertyType) ||
				!isSet(matter?.propertyDetails?.propertyType.type)
			) {
				fields.push({
					fieldName: 'Property type',
					url,
				});
			}

			if (!isSet(matter?.propertyDetails?.address?.province)) {
				fields.push({
					fieldName: 'Property address - province',
					url,
				});
			}

			if (
				!isSet(matter?.propertyDetails?.address?.postcode) &&
				checkIfPostalCodeRequired(
					TitleInsuranceProvider.STEWART,
					matter?.propertyDetails,
				)
			) {
				fields.push({
					fieldName: 'Property address - postal code',
					url,
				});
			}

			if (matterTitles.length === 0) {
				fields.push({
					fieldName: 'Legal description',
					url,
				});

				if (
					matter?.propertyDetails?.propertyType?.type === PropertyType.CONDO
				) {
					fields.pop();
				}
			} else {
				matterTitles.forEach((matterTitle) => {
					if (!isSet(matterTitle?.legalDescription)) {
						fields.push({
							fieldName: `Legal description (${matterTitle?.pinNumber})`,
							url,
						});
					}
				});
			}
		} else {
			fields.push({
				fieldName: 'No property on file',
				url,
			});
		}
		return fields;
	}
}
